import type { HistoryRange } from '@wave/shared';

const H = 3600_000;

/**
 * Окно устойчивой линии по диапазону. Подобрано по проду: переводы «в пути»
 * живут от 5 минут до нескольких часов (ручное зеркалирование ног), окно
 * должно быть заметно длиннее типичного выброса своего масштаба, но короче
 * реальной смены уровня, которую глаз должен увидеть.
 */
const WINDOW_BY_RANGE: Record<HistoryRange, number> = {
  '24h': 2 * H,
  '7d': 6 * H,
  '30d': 24 * H,
  '90d': 3 * 24 * H,
  all: 7 * 24 * H,
};

export function robustWindowMs(range: HistoryRange): number {
  return WINDOW_BY_RANGE[range];
}

function medianOf(sorted: number[]): number {
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 1 ? sorted[mid]! : (sorted[mid - 1]! + sorted[mid]!) / 2;
}

/**
 * Скользящая медиана по ВРЕМЕНИ, центрированная: точка i — медиана значений с
 * |ts − ts_i| ≤ window/2. Окно по времени, а не по числу точек: на неровных
 * данных (прореживание истории, пропуски снапшотов) k точек — это то минуты,
 * то сутки. Выброс, живущий меньше половины окна, в медиану не проходит;
 * устойчивая смена уровня проходит с задержкой не больше половины окна.
 */
export function rollingMedianByTime(ts: number[], values: number[], windowMs: number): number[] {
  const half = windowMs / 2;
  const out: number[] = [];
  let lo = 0;
  let hi = 0;
  for (let i = 0; i < values.length; i++) {
    while (ts[lo]! < ts[i]! - half) lo++;
    while (hi + 1 < values.length && ts[hi + 1]! <= ts[i]! + half) hi++;
    out.push(medianOf(values.slice(lo, hi + 1).sort((a, b) => a - b)));
  }
  return out;
}

function quantile(sorted: number[], q: number): number {
  return sorted[Math.floor(q * (sorted.length - 1))]!;
}

function niceStep(span: number): number {
  const magnitude = 10 ** Math.floor(Math.log10(Math.max(span, 1)));
  return magnitude / 2;
}

/**
 * Границы оси Y по «нормальным» значениям всех серий (2–98 перцентиль + запас):
 * одиночный выброс до 957k больше не сжимает рабочий диапазон ~280k в полосу.
 * Запас — не меньше 1% уровня, чтобы ровная линия не прилипала к краю.
 * Меньше 5 точек — null: пусть шкалу решает ECharts.
 */
export function robustAxisRange(series: number[][]): { min: number; max: number } | null {
  const all = series.flat().filter((v) => Number.isFinite(v));
  if (all.length < 5) return null;
  const sorted = [...all].sort((a, b) => a - b);
  const lo = quantile(sorted, 0.02);
  const hi = quantile(sorted, 0.98);
  const level = Math.abs(medianOf(sorted));
  const pad = Math.max((hi - lo) * 0.1, level * 0.01, 1);
  const min = lo - pad;
  const max = hi + pad;
  const step = niceStep(max - min);
  return { min: Math.floor(min / step) * step, max: Math.ceil(max / step) * step };
}

export interface ValueItem {
  key: string;
  label: string;
  value: number;
}

/** Топ-N по модулю, остальное — одной строкой «Прочие (k)». */
export function groupTopItems(items: ValueItem[], n: number): ValueItem[] {
  const sorted = [...items].sort((a, b) => Math.abs(b.value) - Math.abs(a.value));
  if (sorted.length <= n) return sorted;
  const rest = sorted.slice(n);
  return [
    ...sorted.slice(0, n),
    { key: '__rest', label: `Прочие (${rest.length})`, value: rest.reduce((acc, it) => acc + it.value, 0) },
  ];
}
